import React, { useMemo, useCallback, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import { Button } from 'design-system';
import Spacer from 'design-system/Spacer';
import BlurAmountView from 'design-system/BlurAmountView';
import Amplitude from 'utils/amplitude';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import usePasscodeModal from 'hooks/usePasscodeModal';
import { useStockMeta } from '../hooks';
import TapableText from './TapableText';
import AboutInfoRow from './AboutInfoRow';
import { formatNextBuyDate } from '../utils';
import { amplitudeSource } from '../utils/analytics';
import { orderConfirmViewStrings } from './OrderConfirmView';
import useRecurringOrderPreview from '../hooks/useRecurringOrderPreview';
import { RecurringBuyInterval, Weekdays, } from '../types';
import FXFeeRow from './FXFeeRow';
import { selectUserFxFee } from '../reducers/selectors';
import RowAmount from './RowAmount';
const weekdayTextMap = {
    [Weekdays.MONDAY]: 'Monday',
    [Weekdays.TUESDAY]: 'Tuesday',
    [Weekdays.WEDNESDAY]: 'Wednesday',
    [Weekdays.THURSDAY]: 'Thursday',
    [Weekdays.FRIDAY]: 'Friday',
};
const RecurringBuyOrderConfirmView = ({ amount, execute, assetId, buyConfig, totalAmount, fxConversion, nonTradingDayPreference, }) => {
    const [loading, nextBuy] = useRecurringOrderPreview({
        amount: amount.value,
        assetId,
        buyConfig,
        nonTradingDayPreference,
    });
    const colors = useColors();
    const [getPin] = usePasscodeModal();
    const stockCache = useStockMeta(assetId);
    const userFxFees = useAppSelector(selectUserFxFee);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const symbol = stockCache?.symbol;
    useEffect(() => {
        if (symbol) {
            Amplitude.logEvent('Open Order Preview', { source: amplitudeSource, type: 'recurring_buy', symbol });
        }
    }, [symbol]);
    const handleConfirm = useCallback(async (forcePinAuthentication) => {
        if (symbol) {
            const biometryDescription = `You are adding recurring order of ${formatAmount(amount.value, amount.currency, 2, false)} for $${symbol}`;
            try {
                Amplitude.logEvent('Submitted Request', { source: amplitudeSource, type: 'recurring_buy', symbol });
                const pin = await getPin({
                    title: 'Enter Your Pin Code',
                    biometryDescription,
                    forcePinAuthentication: typeof forcePinAuthentication === 'boolean' ? forcePinAuthentication : false,
                });
                try {
                    await execute({ pin, symbol, amount, assetId, buyConfig, nonTradingDayPreference });
                }
                catch (e) {
                    // Other errors are already handled in execute
                    if ('message' in e && e.message === 'Refresh token expired') {
                        handleConfirm(true);
                    }
                }
            }
            catch {
                // use cancelled
            }
        }
    }, [amount, assetId, buyConfig, execute, getPin, nonTradingDayPreference, symbol]);
    const intervalText = useMemo(() => {
        switch (buyConfig.interval) {
            case RecurringBuyInterval.WEEKLY:
                return `Every week on ${weekdayTextMap[buyConfig.day]}`;
            case RecurringBuyInterval.MONTHLY:
                return 'Once every month';
            case RecurringBuyInterval.QUARTERLY:
                return 'Once every 3 months';
            default:
                // should never reach here
                return '';
        }
    }, [buyConfig]);
    return (<View>
      <Spacer h={46}/>
      <View style={styles.row}>
        <Text Text-20 flex>
          {symbol ? `Buy ${symbol}` : 'Buy'}
        </Text>
        <Logo R44 uri={stockCache?.iconUrl}/>
      </View>
      <Spacer h={8}/>
      <AboutInfoRow title="Order type" info="Recurring market order" noPaddingHorizontal/>
      <AboutInfoRow title="Interval" info={intervalText} noPaddingHorizontal/>
      <AboutInfoRow noPaddingHorizontal requesting={loading} title="First execution" info={nextBuy ? formatNextBuyDate(nextBuy) : ''}/>
      {!!userFxFees && <FXFeeRow fxConversion={fxConversion}/>}
      <BlurAmountView enabled={scrambled}>
        <RowAmount est bold showCents noBottomLine noPaddingHorizontal testID="rowTotalCost" amount={totalAmount} title={orderConfirmViewStrings.totalCost}/>
      </BlurAmountView>
      <Spacer h={16}/>
      <TapableText pdf Gray centered TextThin-12 linkColor={colors.text.brand} links={orderConfirmViewStrings.links} text={orderConfirmViewStrings.conditions}/>
      <Spacer h={16}/>
      <Button title={orderConfirmViewStrings.buttonTitle} brand onPress={handleConfirm}/>
    </View>);
};
export default RecurringBuyOrderConfirmView;
const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
});
